import { useQueryClient } from "@tanstack/react-query"
import {
	CHAT_EVENT_NAMES,
	MARECZEK_EVENT_NAMES,
	type RealtimeMessage,
} from "@zplanem/responses"
import { useEffect } from "react"
import { getPusherClient } from "../realtime/pusher-client"
import { applyRealtimeToChatCaches } from "./use-chat"
import { applyMareczekEventToThinkingCache } from "./use-mareczek-thinking"

/**
 * Realtime for the project chat rail: the shared broadcast channel plus
 * the caller's per-user project channel (acknowledge events from other
 * devices). Mirrors the property pair of hooks in one place.
 */
export function useProjectChatChannels(
	propertyId: string | undefined,
	projectId: string | undefined,
	userId: string | undefined,
): void {
	const queryClient = useQueryClient()
	useEffect(() => {
		if (!propertyId || !projectId || !userId) return
		const names = [
			`private-project-${projectId}-chat`,
			`private-project-${projectId}-user-${userId}-chat`,
		]
		const channels = names.map((name) => getPusherClient().subscribe(name))
		const handler = (data: unknown) =>
			applyRealtimeToChatCaches(
				queryClient,
				propertyId,
				userId,
				data as RealtimeMessage,
				projectId,
			)
		// Mareczek "thinking" indicator for the project chat (ZPL-152) — pushed
		// on the shared project channel; route by payload threadId.
		const mareczekHandlers = MARECZEK_EVENT_NAMES.map(
			(eventName) => (data: unknown) => {
				applyMareczekEventToThinkingCache(queryClient, {
					event: eventName,
					payload: data,
				} as RealtimeMessage)
			},
		)
		for (const channel of channels) {
			for (const eventName of CHAT_EVENT_NAMES) {
				channel.bind(eventName, handler)
			}
			for (const [i, eventName] of MARECZEK_EVENT_NAMES.entries()) {
				channel.bind(eventName, mareczekHandlers[i])
			}
		}
		return () => {
			for (const [i, channel] of channels.entries()) {
				for (const eventName of CHAT_EVENT_NAMES) {
					channel.unbind(eventName, handler)
				}
				for (const [j, eventName] of MARECZEK_EVENT_NAMES.entries()) {
					channel.unbind(eventName, mareczekHandlers[j])
				}
				getPusherClient().unsubscribe(names[i])
			}
		}
	}, [propertyId, projectId, userId, queryClient])
}
